import React from 'react';
import type { ShippingResult } from '../../utils/shipping';

type TFn = (key: string, fallback: string, params?: Record<string, string | number>) => string;

interface ShippingSelectorProps {
  t: TFn;
  enabledCountries: Array<{ code: string; name: string; enabled: boolean; zones: unknown[] }>;
  shippingCountryUI: string;
  shippingPostalCodeUI: string;
  shippingResult: ShippingResult | null;
  /** True while a country with zones still has no postal code: nothing is priced yet. */
  addressPending: boolean;
  /** False for a country without zones: the postal code changes nothing there, so the input is hidden. */
  postalCodeApplies: boolean;
  unsupportedMessage?: string;
  onCountryChange: (country: string) => void;
  onPostalCodeChange: (postalCode: string) => void;
}

const FIELD_CLASS =
  'min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-1 font-poppins text-xs text-black/80 focus:border-[#008BBC] focus:outline-none';

/** Compact shipping address (country + postal code) shown under the shipping line of the
 *  cart summary. The address is optional: it only refines the shipping price shown. */
const ShippingSelector: React.FC<ShippingSelectorProps> = ({
  t,
  enabledCountries,
  shippingCountryUI,
  shippingPostalCodeUI,
  shippingResult,
  addressPending,
  postalCodeApplies,
  unsupportedMessage,
  onCountryChange,
  onPostalCodeChange,
}) => {
  const unsupported = !addressPending && shippingResult !== null && !shippingResult.supported;
  const showThreshold =
    !addressPending &&
    shippingResult?.supported === true &&
    !shippingResult.is_free &&
    shippingResult.free_shipping_threshold !== null &&
    shippingResult.free_shipping_threshold !== undefined;
  return (
    <div data-testid="shipping-selector" className="mt-1.5 space-y-1">
      <div className="flex gap-2">
        <select
          aria-label={t('step13c.shipping_country', 'País')}
          value={shippingCountryUI}
          onChange={(e) => onCountryChange(e.target.value)}
          className={`${FIELD_CLASS} ${postalCodeApplies ? 'w-2/5' : 'w-full'}`}
        >
          {enabledCountries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        {postalCodeApplies && (
          <input
            type="text"
            inputMode="numeric"
            aria-label={t('step13c.shipping_postal', 'Código postal')}
            value={shippingPostalCodeUI}
            onChange={(e) => onPostalCodeChange(e.target.value.trim())}
            placeholder={t('step13c.shipping_postal', 'Código postal')}
            className={`${FIELD_CLASS} flex-1`}
          />
        )}
      </div>
      {showThreshold && shippingResult && (
        <p className="font-poppins text-[11px] text-black/45">
          {t('step13c.shipping_free_threshold', 'Gratis a partir de {amount} €', {
            amount: shippingResult.free_shipping_threshold as number,
          })}
        </p>
      )}
      {unsupported && (
        <p role="alert" className="font-poppins text-[11px] leading-snug text-amber-800">
          {unsupportedMessage || t('step13c.shipping_unsupported', 'Ahora mismo no enviamos a tu zona.')}
        </p>
      )}
    </div>
  );
};

export default ShippingSelector;
